import React, { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { TentativasLoginService } from '../../services';
import { Search, Trash2, Eye, Shield, AlertTriangle, Calendar, User, MapPin } from 'lucide-react';
import toast from 'react-hot-toast';

interface LoginAttempt {
  id: number;
  email: string;
  ip: string;
  tentativas: number;
  bloqueado_ate?: string;
  created_at?: string;
  updated_at?: string;
}

interface Filters {
  dataInicio: string;
  dataFim: string;
}

const LoginAttempts: React.FC = () => {
  const { usuario } = useAuth();
  const [attempts, setAttempts] = useState<LoginAttempt[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [filters, setFilters] = useState<Filters>({
    dataInicio: '',
    dataFim: ''
  });

  // Verificar se o usuário tem nível 'dev'
  if (!usuario || usuario.nivel !== 'dev') {
    return <Navigate to="/" replace />;
  }

  useEffect(() => {
    loadLoginAttempts();
  }, []);

  const loadLoginAttempts = async (): Promise<void> => {
    try {
      setIsLoading(true);
      const response = await TentativasLoginService.getTentativasLogin();
      setAttempts(response);
    } catch (error) {
      console.error('Erro ao carregar tentativas de login:', error);
      toast.error('Erro ao carregar tentativas de login');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (attemptId: number): Promise<void> => {
    if (confirm('Tem certeza que deseja excluir esta tentativa de login?')) {
      try {
        await TentativasLoginService.deleteTentativaLogin(attemptId);
        toast.success('Tentativa de login excluída com sucesso');
        loadLoginAttempts(); // Recarregar lista
      } catch (error) {
        console.error('Erro ao excluir tentativa de login:', error);
        toast.error('Erro ao excluir tentativa de login');
      }
    }
  };

  const handleClearBlocked = async (): Promise<void> => {
    if (confirm('Tem certeza que deseja limpar todas as tentativas bloqueadas?')) {
      try {
        // Como não há uma função específica para limpar bloqueios, vamos apenas mostrar uma mensagem
        toast.success('Funcionalidade de limpar bloqueios será implementada em breve');
        // loadLoginAttempts(); // Recarregar lista
      } catch (error) {
        console.error('Erro ao limpar tentativas bloqueadas:', error);
        toast.error('Erro ao limpar tentativas bloqueadas');
      }
    }
  };

  const filteredAttempts = attempts.filter(attempt => {
    const matchesSearch = attempt.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         attempt.ip.toLowerCase().includes(searchTerm.toLowerCase());
    
    let matchesDate = true;
    if (filters.dataInicio && filters.dataFim) {
      const attemptDate = new Date(attempt.created_at || '');
      const startDate = new Date(filters.dataInicio);
      const endDate = new Date(filters.dataFim);
      matchesDate = attemptDate >= startDate && attemptDate <= endDate;
    }
    
    return matchesSearch && matchesDate;
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 shadow-soft">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Tentativas de Login
            </h1>
            <p className="text-gray-600">
              Monitore e gerencie as tentativas de acesso ao sistema
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <button 
              onClick={handleClearBlocked}
              className="btn btn-warning"
            >
              <Shield className="w-4 h-4 mr-2" />
              Limpar Bloqueios
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 shadow-soft">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Buscar tentativas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Date Range */}
          <div className="flex space-x-2">
            <input
              type="date"
              value={filters.dataInicio}
              onChange={(e) => setFilters({...filters, dataInicio: e.target.value})}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <input
              type="date"
              value={filters.dataFim}
              onChange={(e) => setFilters({...filters, dataFim: e.target.value})}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>
      </div>

      {/* Attempts Table */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-200/50 shadow-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  IP
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tentativas
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data/Hora
                </th>

                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredAttempts.length > 0 ? (
                filteredAttempts.map((attempt) => (
                  <tr key={attempt.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center mr-3">
                          <span className="text-primary-700 font-medium text-sm">
                            {attempt.email.charAt(0) || 'U'}
                          </span>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {attempt.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <MapPin className="w-4 h-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-900 font-mono">
                          {attempt.ip}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 font-medium">
                        {attempt.tentativas}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        attempt.bloqueado_ate && new Date(attempt.bloqueado_ate) > new Date() 
                          ? 'bg-red-100 text-red-800' 
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {attempt.bloqueado_ate && new Date(attempt.bloqueado_ate) > new Date() 
                          ? 'Bloqueado' 
                          : 'Ativo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                        {attempt.created_at ? new Date(attempt.created_at).toLocaleDateString('pt-BR') : 'N/A'}
                      </div>
                      <div className="text-xs text-gray-400">
                        {attempt.created_at ? new Date(attempt.created_at).toLocaleTimeString('pt-BR') : 'N/A'}
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <Link
                          to={`/tentativas-login/${attempt.id}`}
                          className="text-primary-600 hover:text-primary-900"
                        >
                          <Eye className="w-4 h-4" />
                        </Link>
                        <button
                          onClick={() => handleDelete(attempt.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    <Shield className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                    <p className="text-lg font-medium">Nenhuma tentativa encontrada</p>
                    <p className="text-sm">Tente ajustar os filtros de busca</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 shadow-soft">
          <div className="flex items-center">
            <div className="p-3 rounded-xl bg-blue-100 text-blue-600">
              <Shield className="w-6 h-6" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total de Tentativas</p>
              <p className="text-2xl font-bold text-gray-900">{attempts.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 shadow-soft">
          <div className="flex items-center">
            <div className="p-3 rounded-xl bg-green-100 text-green-600">
              <User className="w-6 h-6" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Usuários Únicos</p>
              <p className="text-2xl font-bold text-gray-900">
                {new Set(attempts.map(a => a.email)).size}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 shadow-soft">
          <div className="flex items-center">
            <div className="p-3 rounded-xl bg-red-100 text-red-600">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Bloqueados</p>
              <p className="text-2xl font-bold text-gray-900">
                {attempts.filter(a => a.bloqueado_ate && new Date(a.bloqueado_ate) > new Date()).length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 shadow-soft">
          <div className="flex items-center">
            <div className="p-3 rounded-xl bg-purple-100 text-purple-600">
              <Shield className="w-6 h-6" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Filtradas</p>
              <p className="text-2xl font-bold text-gray-900">{filteredAttempts.length}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginAttempts;
